import { useApolloClient } from "@apollo/client";
import { Button, Flex } from "@chakra-ui/react";
import _ from "lodash";
import React, { useMemo } from "react";
import {
  MultiSelectSchoolRank,
  School,
} from "src/components/Inputs/MultiSelectSchoolRank";
import { useFormContext } from "src/components/Providers/FormProvider";
import { SchoolRankInsertInputType } from "src/hooks/useSchoolRank";
import { useStaffOverrideConfig } from "src/scenes/orgAdmin/forms/hooks/useStaffOverrideConfig";
import { useRemoveAnswersForHiddenQuestions } from "src/scenes/parent/forms/hooks/useRemoveAnswersForHiddenQuestions";
import * as AFF from "src/services/formTemplateFilters";
import * as AF from "src/types/formTemplate";
import { SchoolRankWithTags } from "src/types/schoolRank";
import { GET_SCHOOLS_RANK_VIEW } from "../../../graphql/queries";
import { useUpsertFormSchoolsRank } from "../../../hooks/useUpsertFormSchoolsRank";

type Props = {
  formTemplateId: uuid;
  formId: uuid;
  enrollmentPeriodId: uuid;
  applicant: AFF.Types.Applicant;
  allSections: AF.Sections<AF.WithId>;
  schoolRankingSection: AF.SchoolRankingSection<AF.WithId>;
  schoolsRank: SchoolRankWithTags[];
  onCloseEdit: () => void;
};
export const SchoolRankingEditForm: React.FC<Props> = ({
  formTemplateId,
  formId,
  enrollmentPeriodId,
  applicant,
  allSections,
  schoolRankingSection,
  schoolsRank: initialSchoolsRank,
  onCloseEdit,
}) => {
  const [inMemorySchools, setInMemorySchools] = React.useState<
    readonly School[]
  >(normalizeRankedSchools(initialSchoolsRank));

  const client = useApolloClient();

  const { updateSchoolsRank } = useUpsertFormSchoolsRank(
    initialSchoolsRank.map((item) => ({
      form_id: formId,
      schools_ranking_section_id: schoolRankingSection.id,
      school_id: item.school.id,
      rank: item.rank,
    }))
  );

  const { allowIneligible, overrideConfig } =
    useStaffOverrideConfig(formTemplateId);

  const { refetchAnswers, refetchSelectedSchools } = useFormContext();
  const removeAnswersForHiddenQuestions = useRemoveAnswersForHiddenQuestions({
    formId,
  });

  const [isSaving, setSaving] = React.useState(false);
  const onSave = async () => {
    if (
      !_.isEqual(
        inMemorySchools.map((school) => school.id),
        normalizeRankedSchools(initialSchoolsRank).map((school) => school.id)
      )
    ) {
      setSaving(true);
      const upsertedSchoolRanks = inMemorySchools.map(
        (school): SchoolRankInsertInputType => ({
          form_id: formId,
          schools_ranking_section_id: schoolRankingSection.id,
          school_id: school.id,
          is_override: school.isOverride ?? false,
          override_reason: school.overrideReason ?? null,
        })
      );
      await updateSchoolsRank(
        formId,
        schoolRankingSection.id,
        upsertedSchoolRanks
      );
      await refetchSelectedSchools();
      await removeAnswersForHiddenQuestions();
      await refetchAnswers();
      await client.refetchQueries({ include: [GET_SCHOOLS_RANK_VIEW] });
      setSaving(false);
    }
    onCloseEdit();
  };

  const preRankingSection = useMemo(
    () =>
      allSections.find(
        (section): section is AF.PreRankingSection<AF.WithId> =>
          section?.type === "PreRankingSection"
      ) as AF.PreRankingSection<AF.WithId> | undefined,
    [allSections]
  );

  if (!preRankingSection) return null;

  return (
    <Flex direction="column" gap={3}>
      <Flex maxWidth="28rem">
        <MultiSelectSchoolRank
          formTemplateId={formTemplateId}
          formId={formId}
          applicant={applicant}
          selectedSchools={inMemorySchools}
          preRankingSection={preRankingSection}
          schoolRankingSection={schoolRankingSection}
          enrollmentPeriodId={enrollmentPeriodId}
          allowIneligible={allowIneligible}
          overrideConfig={overrideConfig}
          onSort={async (schools: readonly School[]) => {
            setInMemorySchools(schools);
          }}
          onDelete={async (
            schools: readonly School[],
            deletedSchool: School
          ) => {
            setInMemorySchools(
              schools.filter((school) => school.id !== deletedSchool.id)
            );
          }}
          onSelectedItemsChanged={async (schools: readonly School[]) => {
            setInMemorySchools(schools);
          }}
        />
      </Flex>
      <Flex justifyContent="flex-end" gap={3}>
        <Button
          type="button"
          variant="ghost"
          colorScheme="gray"
          onClick={onCloseEdit}
        >
          Cancel
        </Button>
        <Button type="submit" onClick={onSave} isLoading={isSaving}>
          Save
        </Button>
      </Flex>
    </Flex>
  );
};

export function normalizeRankedSchools(
  schoolsRank: readonly SchoolRankWithTags[]
): School[] {
  return _.sortBy(schoolsRank, (schoolRank) => schoolRank.rank).map(
    (schoolRank) => ({
      ...schoolRank.school,
      isOverride: schoolRank.is_override,
      // `in` narrows the admin/parent rank union so this compiles even though
      // override_reason is only selected on the admin (GET_SCHOOLS_RANK_VIEW)
      // query. Carrying these through is what lets a re-save round-trip the
      // existing override instead of resetting it to false/null.
      overrideReason:
        "override_reason" in schoolRank ? schoolRank.override_reason : null,
    })
  );
}
